import { browserTimezone, shortenTimezone } from "@gigpro/utils/datetime";
import { isValid } from "date-fns";
import { Close } from "../../Icons";
import { Button } from "../Button";
import { DatePicker } from "../DatePicker";
import { DebouncedInput } from "../DebouncedInput";
import { Label } from "../Label";
import { Select, SelectItem } from "../Select";
import type { ColumnLessFilter, Filter, SelectFilterOptions } from "./types";

type FilterValue = string | number | boolean | Date | undefined;

const toFilterValue = (value: unknown): FilterValue =>
  typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value instanceof Date
    ? value
    : undefined;

type ColumnFilterProps = {
  name: string;
  filter: Filter | ColumnLessFilter;
  value: unknown;
  onValueChange: (value: unknown) => void;
};

export function FilterInput({ filter, name, value, onValueChange }: Readonly<ColumnFilterProps>) {
  let input: React.ReactNode;
  const commonProps = { name: filter.name ?? name, value: toFilterValue(value), onValueChange };
  switch (filter.variant) {
    case "text":
      input = <TextInput {...commonProps} />;
      break;
    case "number":
      input = <NumberInput {...commonProps} />;
      break;
    case "date":
      input = <DateInput {...commonProps} />;
      break;
    case "boolean":
      input = <BooleanInput {...commonProps} />;
      break;
    case "select":
      input = <SelectInput {...commonProps} options={filter.options} />;
      break;
    default:
      throw new Error("Unsupported filter variant");
  }

  return filter.name ? (
    <div>
      <Label className="flex gap-0.5">
        {filter.name}{" "}
        <span className="text-small text-gray">
          ({filter.variant}
          {filter.variant === "date" && `, ${shortenTimezone(browserTimezone)}`})
        </span>
      </Label>
      {input}
    </div>
  ) : (
    input
  );
}

type FilterImplProps = Pick<ColumnFilterProps, "name" | "onValueChange"> & { value: FilterValue };

function TextInput({ name, value, onValueChange }: Readonly<FilterImplProps>) {
  return <DebouncedInput placeholder={`Filter ${name}...`} value={`${value ?? ""}`} onValueChange={onValueChange} />;
}

function NumberInput({ name, value, onValueChange }: Readonly<FilterImplProps>) {
  return (
    <DebouncedInput
      type="text"
      inputMode="numeric"
      pattern="\d*"
      placeholder={`Filter ${name}...`}
      value={`${value ?? ""}`}
      onValueChange={onValueChange}
    />
  );
}

function BooleanInput({ name, value, onValueChange }: Readonly<FilterImplProps>) {
  return (
    <div className="flex w-full min-w-0 gap-1">
      <Select
        placeholder={`Select ${name}`}
        value={`${value ?? ""}`}
        onValueChange={(value) => onValueChange(value === "true")}
        className="min-w-0 flex-1"
      >
        <SelectItem value="true">True</SelectItem>
        <SelectItem value="false">False</SelectItem>
      </Select>
      <Button icon disabled={value == null} onClick={() => onValueChange(undefined)}>
        <Close />
      </Button>
    </div>
  );
}

function DateInput({ name, value, onValueChange }: Readonly<FilterImplProps>) {
  return (
    <DatePicker
      placeholder={`Filter ${name}...`}
      value={value !== undefined && typeof value !== "boolean" && isValid(value) ? new Date(value) : undefined}
      onValueChange={onValueChange}
    />
  );
}

function SelectInput({
  name,
  value,
  onValueChange,
  options,
}: Readonly<FilterImplProps & { options: SelectFilterOptions }>) {
  return (
    <div className="flex w-full gap-1">
      <Select
        placeholder={`Select ${name}`}
        value={`${value ?? ""}`}
        onValueChange={(v) => onValueChange(options.find((option) => `${option.value}` === v)?.value)}
        className="flex-1"
      >
        {options.map((option) => (
          <SelectItem key={`${option.value}`} value={`${option.value}`}>
            {option.label}
          </SelectItem>
        ))}
      </Select>
      <Button icon disabled={!value} onClick={() => onValueChange(undefined)}>
        <Close />
      </Button>
    </div>
  );
}
